import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Badge, Button } from "components/ui";
import { getVerificationMetadataLabels, getVerificationStatusInfo } from "../utils/documentUtils";
import {
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";

const STATUS_OPTIONS = [
  { value: 1, label: "Verified" },
  { value: 2, label: "Rejected" },
];

const DocumentVerificationModal = ({
  isOpen,
  onClose,
  document,
  action = "approve",
  onSubmit,
  isSubmitting = false,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(1);
  const [remark, setRemark] = useState("");

  const metadata = useMemo(() => {
    return document ? getVerificationMetadataLabels(document.verificationStatus) : null;
  }, [document]);

  const actionLabel = action === "approve" ? "Approve" : "Reject";
  const statusInfo = useMemo(() => {
    if (!document) return null;
    return getVerificationStatusInfo(document.verificationStatus);
  }, [document]);

  const statusOptions = useMemo(() => {
    if (action === "approve") {
      return STATUS_OPTIONS.filter((option) => option.value === 1);
    }
    return STATUS_OPTIONS.filter((option) => option.value === 2);
  }, [action]);

  useEffect(() => {
    if (action === "approve") {
      setSelectedStatus(1);
    } else {
      setSelectedStatus(2);
    }
    setRemark("");
  }, [action, document]);

  if (!document) {
    return null;
  }

  const handleClose = () => {
    setRemark("");
    onClose?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate remark for rejection
    if ((selectedStatus === 2 || action === "reject") && !remark.trim()) {
      return;
    }

    onSubmit?.({
      action,
      status: selectedStatus,
      remark,
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[300] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={null}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                {action === "approve" ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                )}
                {actionLabel} Document
              </DialogTitle>
              <Button
                onClick={handleClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                disabled={isSubmitting}
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              <form onSubmit={handleSubmit}>
                {/* Document Info Card */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-600">
                      <DocumentIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Document Details
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Name:</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white  ml-2">
                            {document.name}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Current Status:</span>
                          {statusInfo && (
                            <Badge
                              variant="soft"
                              color={statusInfo.color}
                              className="ml-2 text-[11px] font-medium"
                            >
                              {statusInfo.label}
                            </Badge>
                          )}
                        </div>
                        {metadata?.meta?.title && document.verifiedBy && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {metadata.meta.title}:
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white ml-2">
                              {document.verifiedBy}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Selection */}
                <div className="mt-6">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    New Verification Status
                  </label>

                  {action === "approve" ? (
                    <div className="space-y-3">
                      {statusOptions.map((option) => {
                        const isSelected = selectedStatus === option.value;
                        const isVerified = option.value === 1;
                        const isRejected = option.value === 2;

                        return (
                          <label
                            key={option.value}
                            className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                          >
                            <input
                              type="radio"
                              name="status"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => setSelectedStatus(parseInt(e.target.value))}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <span className={`text-sm font-medium ${isSelected
                                ? isVerified
                                  ? "text-green-900 dark:text-green-100"
                                  : "text-red-900 dark:text-red-100"
                                : "text-gray-900 dark:text-white"
                                }`}>
                                {option.label}
                              </span>
                              <p className={`text-xs ${isSelected
                                ? isVerified
                                  ? "text-green-700 dark:text-green-300"
                                  : "text-red-700 dark:text-red-300"
                                : "text-gray-500 dark:text-gray-400"
                                }`}>
                                {isVerified ? "Mark as verified" : "Mark as rejected"}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-red-500 p-2 text-white">
                          <XCircleIcon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium text-red-900 dark:text-red-100">Rejected</p>
                          <p className="text-xs text-red-700 dark:text-red-300">Document will be marked as rejected</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Remark Section */}
                <div className="mt-6">
                  <label htmlFor="verification-remark" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Remark {(selectedStatus === 2 || action === "reject") && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <textarea
                      id="verification-remark"
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      rows={4}
                      required={(selectedStatus === 2 || action === "reject")}
                      className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-2 resize-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${(selectedStatus === 2 || action === "reject") && !remark.trim()
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100 dark:border-red-600 dark:focus:border-red-400 dark:focus:ring-red-900/20"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-100 dark:focus:border-blue-400 dark:focus:ring-blue-900/20"
                        }`}
                      placeholder={
                        (selectedStatus === 2 || action === "reject")
                          ? "Please provide a reason for rejection..."
                          : "Add any notes about this verification decision..."
                      }
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                      {remark.length} characters
                    </div>
                  </div>
                  {(selectedStatus === 2 || action === "reject") && !remark.trim() && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      Rejection remark is required. Please provide a reason for rejecting this document.
                    </p>
                  )}
                </div>

                {/* Warning for rejection */}
                {action === "reject" && (
                  <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                    <div className="flex gap-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          Rejection Notice
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          This action will mark the document as rejected. Please ensure this is the intended action.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                type="button"
                onClick={handleClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color={action === "approve" ? "primary" : "error"}
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting || ((selectedStatus === 2 || action === "reject") && !remark.trim())}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Processing...
                  </span>
                ) : (
                  actionLabel
                )}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default DocumentVerificationModal;
